import React, { useRef, useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import "../styles/dateRangePickerr.css";

const DateRangePickerr = ({ onApplyDateFilter, filterType, carrier }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectionRange, setSelectionRange] = useState({
    startDate: moment().subtract(29, 'days').toDate(),
    endDate: moment().toDate(),
  });
  const [tempRange, setTempRange] = useState(selectionRange);
  const [leftMonth, setLeftMonth] = useState(moment().subtract(1, 'month').startOf('month'));
  const [rightMonth, setRightMonth] = useState(moment().startOf('month'));
  const [activeSection, setActiveSection] = useState('ranges');
  const [isSelectingNew, setIsSelectingNew] = useState(false);

  const pickerRef = useRef();
  const containerRef = useRef();
  const rangesRef = useRef([]);
  const calendarRefs = useRef([]);
  const leftPrevRef = useRef();     
  const rightPrevRef = useRef();

  const allRanges = [
    { label: 'Today', getRange: () => [moment(), moment()] },
    { label: 'Yesterday', getRange: () => [moment().subtract(1, 'days'), moment().subtract(1, 'days')] },
    { label: 'Last 7 Days', getRange: () => [moment().subtract(6, 'days'), moment()] },
    { label: 'Last 30 Days', getRange: () => [moment().subtract(29, 'days'), moment()] },
    { label: 'This Month', getRange: () => [moment().startOf('month'), moment().endOf('month')] },
    { label: 'Last Month', getRange: () => [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month')
      ]
    },
    { label: 'Custom Range', getRange: () => null, isCustom: true },
  ];

  const ranges = carrier === 'Q5'
    ? allRanges.filter(r =>
        r.label !== 'Today' &&
        r.label !== 'Yesterday' &&
        r.label !== 'Last 30 Days' &&
        r.label !== 'This Month'
      )
    : allRanges;

  const generateCalendar = useCallback((month) => {
    const startDate = moment(month).startOf('month').startOf('week');
    const endDate = moment(month).endOf('month').endOf('week');
    const calendar = [];
    const current = startDate.clone();

    while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
      calendar.push(current.clone());
      current.add(1, 'day');
    }

    return calendar;
  }, []);

  const leftCalendarDates = generateCalendar(leftMonth);
  const rightCalendarDates = generateCalendar(rightMonth);

  useEffect(() => {
    rangesRef.current = rangesRef.current.slice(0, ranges.length);

    const totalCalendarDates =
      leftCalendarDates.length + rightCalendarDates.length;

    calendarRefs.current = Array(totalCalendarDates)
      .fill()
      .map((_, i) => calendarRefs.current[i] || React.createRef());
  }, [leftCalendarDates.length, rightCalendarDates.length, ranges.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setActiveSection('ranges');
        setIsSelectingNew(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setActiveSection('ranges');
        setIsSelectingNew(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const navigateMonth = (calendar, direction) => {
    if (calendar === 'left') {
      setLeftMonth((prev) => prev.clone().add(direction, 'month'));
    } else {
      setRightMonth((prev) => prev.clone().add(direction, 'month'));
    }
  };

  const handleRangeSelect = (range, index) => {
    if (range.isCustom) {
      setActiveSection('custom');
      // Keep the current selection when opening custom range
      setTempRange(selectionRange);
      setLeftMonth(moment().subtract(1, 'month').startOf('month'));
      setRightMonth(moment().startOf('month'));
      return;
    }

    const [start, end] = range.getRange();
    const newRange = {
      startDate: start.toDate(),
      endDate: end.toDate(),
    };

    setTempRange(newRange);
    setSelectionRange(newRange);
    onApplyDateFilter?.(filterType, newRange);
    setIsOpen(false);
    setActiveSection('ranges');
  };

  const handleDateSelect = (date) => {
    const momentDate = moment(date);
    let newRange = { ...tempRange };

    // When starting a new selection, mark it and clear old selection
    if (!newRange.startDate || (newRange.startDate && newRange.endDate) || !isSelectingNew) {
      newRange = { startDate: momentDate.toDate(), endDate: null };
      setIsSelectingNew(true);
    } else if (newRange.startDate && !newRange.endDate) {
      const startMoment = moment(newRange.startDate);

      if (momentDate.isBefore(startMoment)) {
        newRange = {
          startDate: momentDate.toDate(),
          endDate: startMoment.toDate(),
        };
      } else {
        newRange.endDate = momentDate.toDate();
      }
    }

    setTempRange(newRange);
  };

  const handleApply = () => {
    let finalRange = { ...tempRange };

    if (activeSection === 'custom') {
      if (finalRange.startDate && !finalRange.endDate) {
        finalRange.endDate = finalRange.startDate;
      } else if (!finalRange.startDate && finalRange.endDate) {
        finalRange.startDate = finalRange.endDate;
      }
    }

    if (!finalRange.startDate || !finalRange.endDate) return;

    setSelectionRange(finalRange);
    onApplyDateFilter?.(filterType, finalRange);
    setIsOpen(false);
    setActiveSection('ranges');
    setIsSelectingNew(false);
  };

  const handleCancel = () => {
    setTempRange(selectionRange);
    setActiveSection('ranges');
    setIsSelectingNew(false);
  };

  const handleRangeKeyDown = (e, index) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        rangesRef.current[index - 1]?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        rangesRef.current[index + 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleRangeSelect(ranges[index], index);
        break;
    }
  };

  const getGlobalIndex = (calendarType, localIndex) =>
    calendarType === 'left'
      ? localIndex
      : localIndex + leftCalendarDates.length;

  const getCalendarInfo = (globalIndex) => {
    if (globalIndex < leftCalendarDates.length) {
      return {
        type: 'left',
        localIndex: globalIndex,
        dates: leftCalendarDates,
      };
    }
    return {
      type: 'right',
      localIndex: globalIndex - leftCalendarDates.length,
      dates: rightCalendarDates,
    };
  };

  const handleCalendarKeyDown = (e, date, localIndex, calendarType) => {
    const globalIndex = getGlobalIndex(calendarType, localIndex);
    const { dates: currentCalendarDates, type: currentType } =
      getCalendarInfo(globalIndex);

    const totalDates = leftCalendarDates.length + rightCalendarDates.length;
    const columns = 7;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        const prev = globalIndex === 0 ? totalDates - 1 : globalIndex - 1;
        calendarRefs.current[prev]?.current?.focus();
        break;

      case 'ArrowRight':
        e.preventDefault();
        const next = globalIndex === totalDates - 1 ? 0 : globalIndex + 1;
        calendarRefs.current[next]?.current?.focus();
        break;

      case 'ArrowUp':
        e.preventDefault();
        const up = globalIndex - columns;
        if (calendarRefs.current[up]) {
          calendarRefs.current[up].current.focus();
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        const down = globalIndex + columns;
        if (calendarRefs.current[down]) {
          calendarRefs.current[down].current.focus();
        }
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDateSelect(date);
        break;
    }
  };

  const handleButtonKeyDown = (e, type) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      type === 'apply' ? handleApply() : handleCancel();
    }
  };

  const handleMonthNavKeyDown = (e, calendar, direction) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigateMonth(calendar, direction);
    }
  };

  const isDateInRange = (date) => {
    const momentDate = moment(date).startOf('day');
    
    if (activeSection === 'custom') {
      // If user has started a new selection, only use tempRange
      if (isSelectingNew) {
        if (tempRange.startDate && tempRange.endDate) {
          const start = moment(tempRange.startDate).startOf('day');
          const end = moment(tempRange.endDate).endOf('day');
          return momentDate.isBetween(start, end, 'day', '[]');
        }
        return false;
      }
      
      // Fall back to selectionRange for initial display (before user starts selecting)
      if (selectionRange.startDate && selectionRange.endDate) {
        const start = moment(selectionRange.startDate).startOf('day');
        const end = moment(selectionRange.endDate).endOf('day');
        return momentDate.isBetween(start, end, 'day', '[]');
      }
      
      return false;
    }
    
    // Use selectionRange for default ranges
    if (selectionRange.startDate && selectionRange.endDate) {
      const start = moment(selectionRange.startDate).startOf('day');
      const end = moment(selectionRange.endDate).endOf('day');
      return momentDate.isBetween(start, end, 'day', '[]');
    }
    
    return false;
  };

  const isDateSelected = (date) => {
    const momentDate = moment(date).startOf('day');
    
    // Use tempRange for manual selection in custom mode
    if (activeSection === 'custom') {
      // If user has started a new selection, only use tempRange
      if (isSelectingNew) {
        const start = tempRange.startDate ? moment(tempRange.startDate).startOf('day') : null;
        const end = tempRange.endDate ? moment(tempRange.endDate).startOf('day') : null;

        const isStart = start && momentDate.isSame(start, 'day');
        const isEnd = end && momentDate.isSame(end, 'day');
        
        return isStart || isEnd;
      }
      
      // Fall back to selectionRange for initial display (before user starts selecting)
      const selStart = selectionRange.startDate ? moment(selectionRange.startDate).startOf('day') : null;
      const selEnd = selectionRange.endDate ? moment(selectionRange.endDate).startOf('day') : null;

      const isSelStart = selStart && momentDate.isSame(selStart, 'day');
      const isSelEnd = selEnd && momentDate.isSame(selEnd, 'day');
      
      return isSelStart || isSelEnd;
    }
    
    // Use selectionRange for default ranges
    const start = selectionRange.startDate ? moment(selectionRange.startDate).startOf('day') : null;
    const end = selectionRange.endDate ? moment(selectionRange.endDate).startOf('day') : null;

    const isStart = start && momentDate.isSame(start, 'day');
    const isEnd = end && momentDate.isSame(end, 'day');
    
    return isStart || isEnd;
  };

  const isCurrentMonth = (date, month) =>
    date.month() === month.month() && date.year() === month.year();

  const isRangeActive = (range) => {
    if (range.isCustom) return activeSection === 'custom';

    const [start, end] = range.getRange();
    
    return (
      moment(selectionRange.startDate).isSame(start, 'day') &&
      moment(selectionRange.endDate).isSame(end, 'day')
    );
  };

  const getDateClassName = (date, month) => {
    const isCurrent = isCurrentMonth(date, month);
    const isInRange = isDateInRange(date);
    const isSelected = isDateSelected(date);
    
    let className = 'calendar-date';
    
    if (!isCurrent) className += ' off';
    if (isInRange) className += ' in-range';
    if (isSelected) className += ' selected';
    
    return className;
  };

  return (
    <div className="date-range-picker-container">
      <div className="daterangepicker-wrapper">
        <div
          ref={pickerRef}
          className="float-end reportrange"
          tabIndex={0}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label="Date range picker"
          onClick={() => {
            setIsOpen(!isOpen);
            setActiveSection('ranges');
            setIsSelectingNew(false);
            setLeftMonth(moment().subtract(1, 'month').startOf('month'));
            setRightMonth(moment().startOf('month'));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
              setActiveSection('ranges');
              setIsSelectingNew(false);
            }
          }}
        >
          <i className="fe fe-calendar fe-14 me-1 navy-icon" aria-hidden="true" />
          <span className="fw-medium" tabIndex={0}>
            {moment(selectionRange.startDate).format('MMM D, YYYY') + ' - ' + moment(selectionRange.endDate).format('MMM D, YYYY')}
          </span>
        </div>

        {isOpen && (
          <>
            <div
              className="daterangepicker-backdrop"
              onClick={() => {
                setIsOpen(false);
                setActiveSection('ranges');
                setIsSelectingNew(false);
              }}
            />

            <div
              ref={containerRef}
              className={`daterangepicker-custom show ensure-on-top ${
                activeSection === 'custom'
                  ? 'with-calendars'
                  : 'without-calendars'
              }`}
              role="dialog"
            >
              {activeSection === 'ranges' ? (
                <div className="ranges-only-section">
                  <div className="ranges">
                    <ul>
                      {ranges.map((range, index) => (
                        <li key={range.label}>
                          <button
                            ref={(el) => (rangesRef.current[index] = el)}
                            className={`range-item ${
                              isRangeActive(range) ? 'active' : ''
                            }`}
                            tabIndex={0}
                            onClick={() => handleRangeSelect(range, index)}
                            onKeyDown={(e) =>
                              handleRangeKeyDown(e, index)
                            }
                          >
                            {range.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  <div className="picker-content">
                    <div className="ranges-section">
                      <div className="ranges">
                        <ul>
                          {ranges.map((range, index) => (
                            <li key={range.label}>
                              <button
                                ref={(el) =>
                                  (rangesRef.current[index] = el)
                                }
                                className={`range-item ${
                                  isRangeActive(range)
                                    ? 'active'
                                    : ''
                                }`}
                                tabIndex={0}
                                onClick={() =>
                                  handleRangeSelect(range, index)
                                }
                                onKeyDown={(e) =>
                                  handleRangeKeyDown(e, index)
                                }
                              >
                                {range.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="custom-range-section">
                      <div className="calendars-container">
                        {/* LEFT CALENDAR */}
                        <div className="calendar-wrapper">
                          <div className="calendar-header">
                            <button
                              ref={leftPrevRef}
                              className="nav-button prev"
                              onClick={() =>
                                navigateMonth('left', -1)
                              }
                              onKeyDown={(e) =>
                                handleMonthNavKeyDown(
                                  e,
                                  'left',
                                  -1
                                )
                              }
                            >
                              ‹
                            </button>

                            <span className="current-month">
                              {leftMonth.format('MMMM YYYY')}
                            </span>

                            <button
                              className="nav-button next"
                              onClick={() =>
                                navigateMonth('left', 1)
                              }
                              onKeyDown={(e) =>
                                handleMonthNavKeyDown(
                                  e,
                                  'left',
                                  1
                                )
                              }
                            >
                              ›
                            </button>
                          </div>

                          <div className="calendar-grid">
                            {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d)=>(
                              <div key={d} className="day-header">
                                {d}
                              </div>
                            ))}

                            {leftCalendarDates.map((date, index) => {
                              const globalIndex = getGlobalIndex(
                                'left',
                                index
                              );

                              return (
                                <button
                                  key={`left-${date.format(
                                    'YYYY-MM-DD'
                                  )}`}
                                  ref={
                                    calendarRefs.current[globalIndex]
                                  }
                                  disabled={!isCurrentMonth(date, leftMonth)}
                                  tabIndex={isCurrentMonth(date, leftMonth) ? 0 : -1}
                                  className={getDateClassName(date, leftMonth)}
                                  onClick={() =>
                                    isCurrentMonth(date, leftMonth) &&
                                    handleDateSelect(date)
                                  }
                                  onKeyDown={(e) =>
                                    isCurrentMonth(date, leftMonth) &&
                                    handleCalendarKeyDown(
                                      e,
                                      date,
                                      index,
                                      'left'
                                    )
                                  }
                                >
                                  {date.date()}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* RIGHT CALENDAR */}
                        <div className="calendar-wrapper">
                          <div className="calendar-header">
                            <button
                              ref={rightPrevRef}
                              className="nav-button prev"
                              onClick={() =>
                                navigateMonth('right', -1)
                              }
                              onKeyDown={(e) =>
                                handleMonthNavKeyDown(
                                  e,
                                  'right',
                                  -1
                                )
                              }
                            >
                              ‹
                            </button>

                            <span className="current-month">
                              {rightMonth.format('MMMM YYYY')}
                            </span>

                            <button
                              className="nav-button next"
                              onClick={() =>
                                navigateMonth('right', 1)
                              }
                              onKeyDown={(e) =>
                                handleMonthNavKeyDown(
                                  e,
                                  'right',
                                  1
                                )
                              }
                            >
                              ›
                            </button>
                          </div>

                          <div className="calendar-grid">
                            {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d)=>(
                              <div key={d} className="day-header">
                                {d}
                              </div>
                            ))}

                            {rightCalendarDates.map((date, index) => {
                              const globalIndex = getGlobalIndex(
                                'right',
                                index
                              );

                              return (
                                <button
                                  key={`right-${date.format(
                                    'YYYY-MM-DD'
                                  )}`}
                                  ref={
                                    calendarRefs.current[globalIndex]
                                  }
                                  disabled={!isCurrentMonth(date, rightMonth)}
                                  tabIndex={isCurrentMonth(date, rightMonth) ? 0 : -1}
                                  className={getDateClassName(date, rightMonth)}
                                  onClick={() =>
                                    isCurrentMonth(date, rightMonth) &&
                                    handleDateSelect(date)
                                  }
                                  onKeyDown={(e) =>
                                    isCurrentMonth(date, rightMonth) &&
                                    handleCalendarKeyDown(
                                      e,
                                      date,
                                      index,
                                      'right'
                                    )
                                  }
                                >
                                  {date.date()}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="drp-buttons">
                    <div className="selected-range-display">
                      <span>
                        {tempRange.startDate
                          ? moment(tempRange.startDate).format(
                              'MMM D, YYYY'
                            )
                          : 'Start date'}
                        {' - '}
                        {tempRange.endDate
                          ? moment(tempRange.endDate).format(
                              'MMM D, YYYY'
                            )
                          : 'End date'}
                      </span>
                    </div>

                    <button
                      className="cancel-btn1"
                      onClick={handleCancel}
                      onKeyDown={(e) =>
                        handleButtonKeyDown(e, 'cancel')
                      }
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={handleApply}
                      onKeyDown={(e) =>
                        handleButtonKeyDown(e, 'apply')
                      }
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DateRangePickerr;